import { useEffect, useState } from 'react';
import { CheckSquare, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { tasksApi, ApiError } from '@solara/api-client';
import type { TaskDto } from '@solara/api-client';

const PRIORITY_TONES: Record<string, 'danger' | 'warning' | 'info' | 'neutral'> = {
  URGENT: 'danger',
  HIGH: 'warning',
  MEDIUM: 'info',
  LOW: 'neutral',
};

export default function AdminTasksPage() {
  const { t } = useTranslation('p1');
  const [tasks, setTasks] = useState<TaskDto[]>([]);
  const [scope, setScope] = useState<'mine' | 'team' | 'all'>('all');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void load();
  }, [scope]);

  async function load() {
    try {
      setTasks(await tasksApi.list(scope));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  async function complete(id: string) {
    setBusy(true);
    setError(null);
    try {
      await tasksApi.transition(id, 'COMPLETED');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('tasks.titleAdmin')}</h1>
          <p className="text-fg-muted text-sm">{t('tasks.subtitle')}</p>
        </div>
        <select
          value={scope}
          onChange={(e) => setScope(e.target.value as 'mine' | 'team' | 'all')}
          className="rounded border border-border bg-surface px-3 py-2 text-sm"
        >
          <option value="all">{t('tasks.scopes.all')}</option>
          <option value="mine">{t('tasks.scopes.mine')}</option>
          <option value="team">{t('tasks.scopes.team')}</option>
        </select>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckSquare className="h-4 w-4" />
            {t('tasks.tasksCount', { count: tasks.length })}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {tasks.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('tasks.emptyAdminInbox')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {tasks.map((task) => (
                <li key={task.id} className="py-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium">{task.title}</p>
                      {task.description && <p className="text-xs text-fg-muted">{task.description}</p>}
                      <p className="text-[11px] text-fg-muted">
                        {task.taskType}
                        {task.relatedEntityType ? ` · ${task.relatedEntityType}` : ''}
                        {task.dueDate && (
                          <span>
                            {' '}
                            · {t('tasks.due')} {new Date(task.dueDate).toLocaleDateString()}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge tone={PRIORITY_TONES[task.priority] ?? 'neutral'} className="text-[10px]">
                        {t(`tasks.priorityLabels.${task.priority}`, { defaultValue: task.priority })}
                      </Badge>
                      <Button variant="ghost" onClick={() => void complete(task.id)} disabled={busy}>
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        {t('tasks.complete')}
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
